import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { studioApi, errorKey } from '../data/studioApi';
import { uploadAsset } from '../data/uploads';
import { clearArtDraft, readArtDraft, saveArtDraft } from '../data/entry';
import { ART_RENDERER_VERSION, copyDocumentImages, normalizeDocument, prepareImageFile } from '../features/ArtEditor/headless';
import { isArtProject } from '../enums/formats';

const fromRevision = revision => ({ base_revision_id: revision.id, base_version: revision.version, document: normalizeDocument(revision.document), caption: revision.caption || null,
  template_id: revision.template_id || null });
// 2.9.0: a gallery image says how it entered Studio (`upload` | `generated`) and what it shows
// (null until described).
const toImage = asset => ({ id: asset.id, url: asset.download?.url || null, width: asset.image?.width || 1000, height: asset.image?.height || 1000,
  origin: asset.origin || 'upload', description: asset.description ?? null });
// A community image goes into an art by its public URL (a `library` source); grids show `thumbnail`.
const toCommunityImage = image => ({ id: image.id, kind: 'library', url: image.url, thumbnail: image.thumbnail_url, width: image.width, height: image.height,
  description: image.description, category: image.category });
// 2.13.0: a profile's video (`art_video`): its signed URL, pixel size and length.
const toVideo = asset => ({ id: asset.id, url: asset.download?.url || null, width: asset.video?.width || 1080, height: asset.video?.height || 1920, duration: asset.video?.duration || 1 });
export const ART_VIDEO_MAX_BYTES = 300 * 1024 * 1024;
export const ART_VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
// Only the filters in use are sent (an empty search is no search).
const filters = ({ origin, category, q }) => {
  const text = (q || '').trim().slice(0, 100);
  return { ...(origin ? { origin } : {}), ...(category ? { category } : {}), ...(text ? { q: text } : {}) };
};
const nextPage = page => (page.current_page < page.last_page ? page.current_page + 1 : null);

// The Studio side of an art project (contract 2.7.0 proposal), the art twin of the script
// editor in useStudio: the working copy (document + caption) of the latest version, kept in
// localStorage until the server accepts it and autosaved 3 s after the last edit. A version
// is what an AI run made: the creator's edits update it in place (`PATCH /art-revisions/{id}`
// with its `version`), they never add one. `409 revision_conflict` → keep mine / load saved.
// It also wires what the editor feature asks of its host: the profile's image gallery and
// brand kit, the template gallery, export persistence and the scope of a chat edit.
export default function useArt({ owner, current, revisions, active, profileId, features, setWorkspace, loadProject, setError, mounted }) {
  const art = isArtProject(current);
  const projectId = art ? current.id : null;
  const latest = art ? revisions.find(revision => revision.id === current.latest_revision_id) || null : null;
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [viewedId, setViewedId] = useState(null);
  const [highlight, setHighlight] = useState(null);
  const [scope, setScope] = useState(null);
  const [brandKit, setBrandKit] = useState(null);
  const draftRef = useRef(null);
  const owned = useRef(null);
  const savingRef = useRef(null);
  const keep = useCallback(next => { draftRef.current = next; setDraft(next); }, []);
  const persistLocal = useCallback(next => {
    try { saveArtDraft(owner, projectId, next); } catch { setError('storage'); }
  }, [owner, projectId, setError]);

  // The saved revision becomes the working copy unless local edits are pending; an AI
  // revision highlights the layers it changed.
  useEffect(() => {
    if (!projectId) { owned.current = null; keep(null); setDirty(false); setViewedId(null); setScope(null); setHighlight(null); return; }
    if (owned.current !== projectId) {
      owned.current = projectId;
      const stored = owner ? readArtDraft(owner, projectId) : null;
      setSaveError(null); setViewedId(null); setScope(null); setHighlight(null);
      keep(stored?.document ? { ...stored, document: normalizeDocument(stored.document) } : latest ? fromRevision(latest) : null);
      setDirty(!!stored?.document);
    } else if (!dirty && latest && (draftRef.current?.base_revision_id !== latest.id || draftRef.current?.base_version !== latest.version)) {
      // A new AI version highlights what it changed; the same version edited elsewhere (another tab) is just adopted.
      const fresh = draftRef.current?.base_revision_id !== latest.id;
      keep(fromRevision(latest));
      if (fresh && latest.changed_layer_ids?.length) setHighlight({ ids: latest.changed_layer_ids, key: latest.id });
    }
  }, [projectId, latest, dirty, owner, keep]);

  const save = useCallback(async () => {
    if (!projectId || !draftRef.current) return false;
    if (!dirty) return { revision: latest, document: draftRef.current.document };
    if (savingRef.current) return savingRef.current;
    const sent = draftRef.current;
    setSaving(true);
    savingRef.current = (async () => {
      try {
        const result = await studioApi.updateArtRevision(sent.base_revision_id, { expected_version: sent.base_version ?? latest?.version,
          document: sent.document, caption: sent.caption, template_id: sent.template_id || null });
        if (!mounted.current) return false;
        // The editor keeps its own document object: only the base version moves, so a save is
        // never taken for an outside change (an undo step) in the editor.
        const same = draftRef.current === sent;
        const next = { ...draftRef.current, base_revision_id: result.id, base_version: result.version };
        keep(next);
        if (same) { setDirty(false); clearArtDraft(owner, projectId); } else persistLocal(next);
        // The version is updated where it is: the list keeps its length and its numbers.
        setWorkspace(previous => (previous.id === projectId ? { ...previous,
          artRevisions: previous.artRevisions.map(revision => (revision.id === result.id ? result : revision)) } : previous));
        setSaveError(null);
        return { revision: result, document: sent.document };
      } catch (failure) {
        const key = errorKey(failure);
        setSaveError(key);
        // The newer revision is read so the editor can offer to keep or discard the draft.
        if (key === 'conflict') loadProject(projectId, { quiet: true });
        return false;
      } finally {
        savingRef.current = null;
        if (mounted.current) setSaving(false);
      }
    })();
    return savingRef.current;
  }, [projectId, dirty, latest, owner, keep, persistLocal, loadProject, setWorkspace, mounted]);

  useEffect(() => {
    if (!dirty || !draft || !projectId || saveError || active) return undefined;
    const timer = setTimeout(save, 3000);
    return () => clearTimeout(timer);
  }, [dirty, draft, projectId, saveError, active, save]);

  const change = useCallback(patch => {
    const next = { ...draftRef.current, ...patch };
    keep(next); setDirty(true); setSaveError(null);
    persistLocal(next);
  }, [keep, persistLocal]);
  const edit = useCallback(document => { change({ document }); setHighlight(null); }, [change]);
  const editCaption = useCallback(caption => change({ caption }), [change]);
  // Pending edits are saved first; null when that fails (nothing unsaved is exported or revised).
  const savedDocument = useCallback(async () => {
    if (!draftRef.current) return null;
    if (!dirty) return draftRef.current.document;
    const result = await save();
    return result ? result.document : null;
  }, [dirty, save]);

  // A conflict: a newer AI version, or this version saved meanwhile from elsewhere.
  const conflict = saveError === 'conflict' && dirty && !!latest && (draft?.base_revision_id !== latest.id || draft?.base_version !== latest.version);
  const keepMine = useCallback(() => {
    const next = { ...draftRef.current, base_revision_id: latest.id, base_version: latest.version };
    keep(next); persistLocal(next); setSaveError(null);
  }, [latest, keep, persistLocal]);
  const loadSaved = useCallback(() => {
    clearArtDraft(owner, projectId);
    keep(fromRevision(latest)); setDirty(false); setSaveError(null);
  }, [owner, projectId, latest, keep]);

  // Older versions are viewed read-only in place; restoring one copies it into the latest
  // version (an edit of it, undoable in the editor).
  const viewed = viewedId && viewedId !== latest?.id ? revisions.find(revision => revision.id === viewedId) || null : null;
  const viewedDocument = useMemo(() => (viewed ? normalizeDocument(viewed.document) : null), [viewed]);
  const view = useCallback(id => setViewedId(id), []);
  const backToLatest = useCallback(() => setViewedId(null), []);
  const restoreViewed = useCallback(() => {
    if (!viewed) return;
    change({ document: normalizeDocument(viewed.document), caption: viewed.caption || null, template_id: viewed.template_id || null });
    setViewedId(null);
  }, [viewed, change]);

  // The chat edit's scope: the layers "Edit with AI" was asked for, until sent or cleared.
  const askAI = useCallback(layers => setScope(layers?.length ? { layerIds: layers.map(layer => layer.id), layers } : null), []);
  const clearScope = useCallback(() => setScope(null), []);
  useEffect(() => {
    if (!scope || !draft?.document) return;
    const ids = new Set(draft.document.pages.flatMap(page => page.layers.map(layer => layer.id)));
    if (!scope.layerIds.every(id => ids.has(id))) setScope(null);
  }, [draft, scope]);

  useEffect(() => {
    if (!profileId || !art) return undefined;
    let alive = true;
    studioApi.brandKit(profileId).then(kit => alive && setBrandKit(kit)).catch(() => alive && setBrandKit(null));
    return () => { alive = false; };
  }, [profileId, art]);
  const saveBrandKit = useCallback(async kit => {
    const saved = await studioApi.updateBrandKit(profileId, { expected_version: brandKit?.version || 1,
      colors: kit.colors.map(({ hex, name, role }) => ({ hex, name: name || null, role })), fonts: kit.fonts,
      logo_asset_id: kit.logo?.asset_id || null, tone: kit.tone?.trim() || null });
    if (mounted.current) setBrandKit(saved);
    return saved;
  }, [profileId, brandKit, mounted]);

  // The profile's image gallery (2.9.0): `list({ cursor, origin, q })` (`origin` `upload` | `generated`,
  // `q` a piece of the description), `upload(file)`, `describe(id, text)` (the creator's words for
  // what it shows), and `community` (`categories()`, `list({ category, q, cursor })`) while
  // `features.community_images` is on, else null.
  const communityImages = !!features.community_images;
  const images = useMemo(() => (profileId ? {
    list: async ({ cursor, origin, q } = {}) => {
      const page = await studioApi.assets({ purpose: 'art_image', profile_id: profileId, ...filters({ origin, q }), page: cursor || 1, per_page: 24 });
      return { items: page.data.filter(asset => asset.download?.url).map(toImage), next: nextPage(page) };
    },
    upload: async file => {
      const prepared = await prepareImageFile(file);
      const asset = await uploadAsset({ purpose: 'art_image', profile_id: profileId, mime_type: prepared.mimeType, image: { width: prepared.width, height: prepared.height } }, prepared.blob);
      return toImage(asset);
    },
    describe: async (id, text) => toImage(await studioApi.describeAsset(id, { description: (text || '').trim() })),
    community: communityImages ? {
      categories: () => studioApi.communityImageCategories().then(response => response.data),
      list: async ({ category, q, cursor } = {}) => {
        const page = await studioApi.communityImages({ ...filters({ category, q }), page: cursor || 1, per_page: 24 });
        return { items: page.data.map(toCommunityImage), next: nextPage(page) };
      },
    } : null,
  } : null), [profileId, communityImages]);

  // The profile's videos (2.13.0, carousels): `list({ cursor })`, newest
  // first, and `upload(file, { width, height, duration, onProgress })` (the editor reads the
  // pixel size and length from the file). The editor trims them; the files stay whole.
  const videos = useMemo(() => (profileId ? {
    maxBytes: ART_VIDEO_MAX_BYTES,
    mimeTypes: ART_VIDEO_MIME_TYPES,
    list: async ({ cursor } = {}) => {
      const page = await studioApi.assets({ purpose: 'art_video', profile_id: profileId, page: cursor || 1, per_page: 24 });
      return { items: page.data.filter(asset => asset.download?.url && asset.video).map(toVideo), next: nextPage(page) };
    },
    upload: async (file, { width, height, duration, onProgress } = {}) => {
      // The sha256 the server checks reads the whole file first: it counts as the first 10%.
      const asset = await uploadAsset({ purpose: 'art_video', profile_id: profileId, mime_type: file.type, video: { width, height, duration } }, file,
        { onProgress: onProgress ? value => onProgress(0.1 + value * 0.9) : undefined });
      return toVideo(asset);
    },
  } : null), [profileId]);

  // Personal templates (2.8.0, `features.art_personal_templates`), for the whole
  // account: `create` (an imported file), `saveArt` (the open art, its version as the
  // template's origin), `update` (rename) and `remove`. `copyImages` brings an imported
  // file's images into the account first, as uploads of this profile's gallery.
  const personalTemplates = !!features.art_personal_templates;
  const gallery = useMemo(() => (profileId ? {
    categories: ({ format }) => studioApi.artTemplateCategories({ format }).then(response => response.data),
    templates: ({ format, category_key: categoryKey, source, per_page: perPage }) => studioApi.artTemplates({ format, profile_id: profileId, ...(categoryKey ? { category_key: categoryKey } : {}), ...(source ? { source } : {}), per_page: perPage }),
    template: id => studioApi.artTemplate(id),
    ...(personalTemplates ? {
      create: body => studioApi.createArtTemplate(body),
      ...(projectId ? { saveArt: body => studioApi.createArtTemplate({ ...body, art_revision_id: draftRef.current?.base_revision_id || null }) } : {}),
      update: (id, body) => studioApi.updateArtTemplate(id, body),
      remove: id => studioApi.deleteArtTemplate(id),
      copyImages: document => copyDocumentImages(document, images.upload),
    } : {}),
  } : null), [profileId, projectId, personalTemplates, images]);

  // Export persistence (`features.exports`): the export freezes the saved revision, the
  // file the editor rendered is uploaded as a private asset and the export completed. A
  // carousel's download keeps one export per page (`page_id`).
  const onExported = useMemo(() => (features.exports && projectId ? async ({ blob, format, scale, mime, pageId }) => {
    const record = await studioApi.createExport(projectId, { art_revision_id: draftRef.current.base_revision_id, format, scale, ...(pageId ? { page_id: pageId } : {}) });
    try {
      const asset = await uploadAsset({ purpose: 'export', project_id: projectId, export_id: record.id, mime_type: mime }, blob);
      return await studioApi.updateExport(record.id, { intent: 'complete', asset_id: asset.id, source_digest: record.source_digest, renderer_version: ART_RENDERER_VERSION });
    } catch (failure) {
      await studioApi.updateExport(record.id, { intent: 'fail', failure_code: 'upload_failed' }).catch(() => null);
      throw failure;
    }
  } : null), [features.exports, projectId]);

  const status = !projectId || !draft ? null : saving ? 'saving' : saveError && saveError !== 'conflict' ? (saveError === 'network' ? 'offline' : 'error') : dirty ? 'pending' : 'saved';
  return useMemo(() => ({
    enabled: art, latest, revisions, document: draft?.document || null, caption: draft?.caption || null,
    dirty, saving, saveError, status, conflict, viewed, viewedDocument, highlight, scope, brandKit, images, videos, gallery, onExported,
    edit, editCaption, save, savedDocument, keepMine, loadSaved, view, backToLatest, restoreViewed, askAI, clearScope, saveBrandKit,
    runOptions: () => (scope?.layerIds?.length ? { target_layer_ids: scope.layerIds } : {}),
  }), [art, latest, revisions, draft, dirty, saving, saveError, status, conflict, viewed, viewedDocument, highlight, scope, brandKit, images, videos, gallery, onExported,
    edit, editCaption, save, savedDocument, keepMine, loadSaved, view, backToLatest, restoreViewed, askAI, clearScope, saveBrandKit]);
}
